import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  MessageSquare, 
  Calendar, 
  UserPlus, 
  FolderOpen,
  GitBranch,
  ArrowRight,
  Check,
  Clock,
  Shield,
  Users,
  Zap,
  Sparkles,
  ChevronRight,
  DollarSign
} from 'lucide-react';

const products = [
  {
    id: 'exhibit-list',
    name: 'Exhibit List.AI',
    description: 'AI-powered exhibit identification and management for trial preparation',
    icon: FileText,
    status: 'live',
    color: 'bg-bronze',
    features: [
      'Upload thousands of documents',
      'AI-powered exhibit identification',
      'Custom exhibit labeling',
      'PDF label stamping',
      'Court-ready reports'
    ],
    price: '$20/month'
  },
  {
    id: 'examination-ai',
    name: 'Examination.AI',
    description: 'AI-powered witness examination outline generation from case documents',
    icon: MessageSquare,
    status: 'live',
    color: 'bg-purple-600',
    features: [
      'AI + OCR document review',
      'Witness identification',
      'Direct examination outlines',
      'Cross examination questions'
    ],
    price: 'Included'
  },
  {
    id: 'billing-ai',
    name: 'Billing.AI',
    description: 'Comprehensive legal billing, IOLTA trust accounting, and AI-powered billing review',
    icon: DollarSign,
    status: 'live',
    color: 'bg-emerald-600',
    features: [
      'IOLTA & Operating accounts',
      'AI billing error detection',
      'Monthly reconciliation',
      'Multi-processor payments'
    ],
    price: '$25/month'
  },
  {
    id: 'intake-ai',
    name: 'Intake.AI',
    description: 'Streamlined client intake, lead tracking, and conversion automation',
    icon: UserPlus,
    status: 'live',
    color: 'bg-emerald-600',
    features: [
      'Kanban lead pipeline',
      'AI consultation recording',
      'Whisper transcription',
      'E-signature agreements'
    ],
    price: '$20/month'
  },
  {
    id: 'case-management',
    name: 'Case Management.AI',
    description: 'Complete case management with documents, billing, and client communication',
    icon: FolderOpen,
    status: 'live',
    color: 'bg-blue-600',
    features: [
      'Matter templates & custom fields',
      'Document management & merge',
      'Time tracking & billing',
      'Client communication log'
    ],
    price: '$30/month'
  },
  {
    id: 'workflow-ai',
    name: 'Workflow.AI',
    description: 'Convert office procedures into visual workflow charts with AI',
    icon: GitBranch,
    status: 'live',
    color: 'bg-violet-600',
    features: [
      'Describe procedures in text',
      'AI-generated workflow charts',
      'Automation recommendations',
      'Share with team roles'
    ],
    price: '$20/month'
  }
];

const benefits = [
  {
    icon: Clock,
    title: 'Save 10+ Hours Per Case',
    description: 'AI automation handles tedious document review so you can focus on strategy and client service.'
  },
  {
    icon: Shield,
    title: 'Bank-Level Security',
    description: 'Your client data is protected with enterprise-grade encryption and compliance standards.'
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Invite associates and paralegals to collaborate on cases with role-based permissions.'
  },
  {
    icon: Zap,
    title: 'Instant AI Analysis',
    description: 'Get intelligent insights from your documents in seconds, not hours.'
  }
];

export default function LandingPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
              <div>
                <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
                <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <a href="#products" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Products
              </a>
              <a href="#benefits" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Benefits
              </a>
              <Link to="/about" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                About
              </Link>
              <a href="#contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </a>
            </nav>

            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/hub">
                    <Button className="bg-navy hover:bg-navy-light">
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="hidden sm:flex">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-navy hover:bg-navy-light">
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-20 lg:py-32">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 border border-navy rounded-full" />
          <div className="absolute top-40 right-20 w-96 h-96 border border-navy rounded-full" />
          <div className="absolute bottom-20 left-1/3 w-48 h-48 border border-navy rounded-full" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 px-4 py-1.5">
              <Sparkles className="w-4 h-4 mr-2 text-bronze" />
              AI-Powered Legal Technology
            </Badge>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6 leading-tight">
              Transform Your Litigation Practice
              <span className="block text-bronze">With Intelligent Software</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Legal AIDynamics provides a suite of AI-powered tools designed specifically for litigation attorneys. 
              Streamline your workflow, reduce costs, and win more cases.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link to="/register">
                <Button size="lg" className="bg-navy hover:bg-navy-light text-base px-8">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#products">
                <Button size="lg" variant="outline" className="text-base px-8">
                  Explore Products
                </Button>
              </a>
            </div>
            
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              7-day free trial • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              Our Product Suite
            </h2>
            <p className="text-lg text-muted-foreground">
              Comprehensive tools for every stage of litigation
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => {
              const Icon = product.icon;
              const isLive = product.status === 'live';
              
              return (
                <Card 
                  key={product.id}
                  className={`relative overflow-hidden transition-all duration-300 ${
                    isLive ? 'border-bronze/50 shadow-lg' : ''
                  }`}
                >
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <Badge 
                      variant={isLive ? "default" : "secondary"}
                      className={isLive ? "bg-green-500" : ""}
                    >
                      {isLive ? 'LIVE' : 'COMING SOON'}
                    </Badge>
                  </div>

                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl ${product.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                    <CardDescription>{product.description}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className={`font-semibold ${isLive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {product.price}
                      </span>
                      {isLive ? (
                        <Link to="/login">
                          <Button className="bg-navy hover:bg-navy-light">
                            Launch App
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      ) : (
                        <Button variant="outline" disabled>
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">
              Why Choose Legal AIDynamics?
            </h2>
            <p className="text-lg text-muted-foreground">
              Built by litigators, for litigators
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <Card key={i} className="text-center border-0 shadow-sm bg-white">
                  <CardContent className="pt-8 pb-6">
                    <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-navy" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Join hundreds of litigation attorneys already using Legal AIDynamics to win more cases.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white text-base px-8">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
                <div>
                  <span className="font-heading text-xl font-bold text-white">Legal AI</span>
                  <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
                </div>
              </div>
              <p className="text-white/60 text-sm max-w-sm">
                AI-powered litigation software for modern law firms. Streamline your practice with intelligent tools designed for trial attorneys.
              </p>
            </div>
            
            {/* Products */}
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/register" className="hover:text-white transition-colors">Exhibit List.AI</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Intake.AI</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Examination.AI</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Workflow.AI</Link></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#benefits" className="hover:text-white transition-colors">About</a></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><a href="mailto:support@legalaidynamics.com" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-white/40">
            © 2025 Legal AIDynamics, LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
